import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import nProgress from "nprogress"
import toast from "react-hot-toast"

import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { usePage } from "contexts/page-context"

import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectDescription from "components/forms/select-description"
import SelectSearchable from "components/forms/select-searchable"

import useStockTransactionRepositories from "repositories/stock-transaction"
import useBranchRepositories from "repositories/branch"
import useProductUnitRepositories from "repositories/product-unit"

import Filter from "./filter"

// ─── helpers ──────────────────────────────────────────────────────────────────

const createEmptyAdjustmentItem = () => ({
  key: `${Date.now()}-${Math.random()}`,
  product_unit_id: "",
  product_unit_name: "",
  physical_stock: "",
  system_stock: null,
  difference_qty: null,
  notes: "",
  is_loading_stock: false,
  stock_loaded: false,
})

const getRowStatus = (item, duplicatedIds = []) => {
  const id = String(item.product_unit_id || "").trim()
  const hasProduct = id !== ""
  const hasPhysical =
    item.physical_stock !== "" &&
    item.physical_stock !== null &&
    item.physical_stock !== undefined &&
    !Number.isNaN(Number(item.physical_stock))

  if (!hasProduct && !hasPhysical) return "empty"
  if (duplicatedIds.includes(id) && id) return "duplicate"
  if (!hasProduct || !hasPhysical) return "incomplete"
  if (item.system_stock === null || item.system_stock === undefined) return "unloaded"
  if (Number(item.difference_qty || 0) === 0) return "unchanged"
  return "changed"
}

const recalcRow = (item, nextPhysical = item.physical_stock, nextSystem = item.system_stock) => {
  const physical =
    nextPhysical === "" || nextPhysical === null || nextPhysical === undefined
      ? null
      : Number(nextPhysical)
  const system =
    nextSystem === "" || nextSystem === null || nextSystem === undefined
      ? null
      : Number(nextSystem)
  return {
    ...item,
    physical_stock: nextPhysical,
    system_stock: nextSystem,
    difference_qty:
      physical === null || Number.isNaN(physical) || system === null || Number.isNaN(system)
        ? null
        : physical - system,
  }
}

// ─── status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  changed:    { label: "CHANGED",    classes: "bg-blue-50   text-blue-700   border-blue-200"   },
  unchanged:  { label: "UNCHANGED",  classes: "bg-gray-50   text-gray-500   border-gray-200"   },
  duplicate:  { label: "DUPLICATE",  classes: "bg-red-50    text-red-700    border-red-200"     },
  incomplete: { label: "INCOMPLETE", classes: "bg-amber-50  text-amber-700  border-amber-200"  },
  unloaded:   { label: "UNLOADED",   classes: "bg-purple-50 text-purple-700 border-purple-200" },
  empty:      { label: "EMPTY",      classes: "bg-white     text-gray-400   border-gray-200"   },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.empty
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold border rounded-full ${cfg.classes}`}>
      {cfg.label}
    </span>
  )
}

// ─── outlet gate ──────────────────────────────────────────────────────────────

function OutletGate({ outletOptions, isLoadingOutletSelection, value, onChange }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-5">
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-100 border border-gray-200">
        <svg className="w-7 h-7 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-gray-800">Select an outlet to begin</p>
        <p className="mt-1 text-sm text-gray-500">
          System stock is outlet-specific. Choose one before adding items.
        </p>
      </div>
      <div className="w-full max-w-xs">
        <SelectDescription
          selection={outletOptions}
          isLoading={isLoadingOutletSelection}
          value={value}
          onChange={onChange}
          keyValue={(v) => v.id}
          title={(v) => v.name}
          description={(v) => v.code || v.id}
          placeholder="Select Outlet..."
        />
      </div>
    </div>
  )
}

// ─── outlet change warning banner ─────────────────────────────────────────────

function OutletChangeWarning({ pendingOutlet, pendingOutletName, onConfirm, onCancel }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-800">
          Switch outlet to <span className="font-bold">{pendingOutletName}</span>?
        </p>
        <p className="mt-0.5 text-sm text-amber-700">
          All system stock values will be re-fetched for the new outlet. Your physical stock entries and notes will be kept.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
          >
            Yes, switch outlet
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── submit blocker ───────────────────────────────────────────────────────────

function SubmitBlocker({ outletId, changedRows, hasInvalidRows, duplicatedIds }) {
  const reasons = []
  if (!outletId)              reasons.push("No outlet selected")
  if (duplicatedIds.length)   reasons.push(`${duplicatedIds.length} duplicate product${duplicatedIds.length > 1 ? "s" : ""}`)
  if (hasInvalidRows)         reasons.push("Some rows have errors (incomplete or unloaded)")
  if (!changedRows.length && outletId) reasons.push("No items with stock changes")

  if (!reasons.length) return null

  return (
    <div className="flex items-start gap-2 text-sm text-gray-500">
      <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span>
        Can&apos;t submit: {reasons.join(" · ")}
      </span>
    </div>
  )
}

// ─── main component ───────────────────────────────────────────────────────────

export const StockAdjustmentContent = ({
  adjustmentId = null,
  initialData = null,
  onClose,
  onSuccess,
  canSubmit = true,
}) => {
  const { configuration } = usePage()

  const [outletId, setOutletId] = useState("")
  const [pendingOutletId, setPendingOutletId] = useState(null)
  const [adjustmentDate, setAdjustmentDate] = useState(moment().format("YYYY-MM-DDTHH:mm"))
  const [adjustmentNotes, setAdjustmentNotes] = useState("")
  const [productSearch, setProductSearch] = useState("")
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("")
  const [selectedProductId, setSelectedProductId] = useState("")  // toolbar combobox selection
  const [selectedProductRow, setSelectedProductRow] = useState(null)
  const [adjustmentItems, setAdjustmentItems] = useState([])
  const [loadingSystemStock, setLoadingSystemStock] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const currentAdjustmentStatus = String(initialData?.adjustment?.status || "").toUpperCase()

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false })

  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const selectedOutlet = useMemo(
    () => outletOptions.find((row) => String(row.id) === String(outletId)),
    [outletId, outletOptions]
  )

  const pendingOutletName = useMemo(() => {
    const found = outletOptions.find((row) => String(row.id) === String(pendingOutletId))
    return found?.name || pendingOutletId
  }, [pendingOutletId, outletOptions])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedProductSearch(productSearch.trim())
    }, 400)

    return () => clearTimeout(timeoutId)
  }, [productSearch])

  const {
    data: productUnitSelectionRaw,
    isLoading: isLoadingProductUnitSelection,
  } = useProductUnitRepositories({
    paginate: true,
    search: debouncedProductSearch || undefined,
  })

  const productUnitOptions = useMemo(() => {
    if (Array.isArray(productUnitSelectionRaw)) return productUnitSelectionRaw
    if (Array.isArray(productUnitSelectionRaw?.data)) return productUnitSelectionRaw.data
    return []
  }, [productUnitSelectionRaw])

  // Set default outlet from config
  useEffect(() => {
    const defaultOutletId =
      configuration?.outlet_id ||
      configuration?.user?.outlet_id ||
      ""
    if (defaultOutletId) setOutletId(String(defaultOutletId))
  }, [configuration])

  useEffect(() => {
    if (!initialData?.adjustment) return

    const adjustment = initialData.adjustment
    const items = Array.isArray(initialData?.items) ? initialData.items : []

    setOutletId(String(adjustment.outlet_id || ""))
    setPendingOutletId(null)
    setAdjustmentDate(
      adjustment.adjustment_date
        ? moment(adjustment.adjustment_date).format("YYYY-MM-DDTHH:mm")
        : moment().format("YYYY-MM-DDTHH:mm")
    )
    setAdjustmentNotes(String(adjustment.notes || ""))
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")
    setAdjustmentItems(
      items.map((item) => ({
        ...createEmptyAdjustmentItem(),
        key: `${item.id || item.reference_id || item.product_unit_id}-${Math.random()}`,
        product_unit_id: String(item.product_unit_id || ""),
        product_unit_name: item.product_unit_name || "",
        physical_stock: String(item.physical_stock ?? ""),
        system_stock: Number(item.system_stock ?? 0),
        difference_qty: Number(item.difference_qty ?? 0),
        notes: item.notes || "",
        stock_loaded: true,
        is_loading_stock: false,
      }))
    )
  }, [initialData])

  // ── derived state ────────────────────────────────────────────────────────

  const selectedProductMap = useMemo(() => {
    const map = {}
    for (const item of adjustmentItems) {
      const id = String(item.product_unit_id || "").trim()
      if (!id) continue
      map[id] = (map[id] || 0) + 1
    }
    return map
  }, [adjustmentItems])

  const duplicatedIds = useMemo(
    () => Object.entries(selectedProductMap).filter(([, count]) => count > 1).map(([id]) => id),
    [selectedProductMap]
  )

  const hasFilledItems = useMemo(
    () => adjustmentItems.some((item) =>
      String(item.product_unit_id || "").trim() !== "" || item.physical_stock !== ""
    ),
    [adjustmentItems]
  )

  const validRows = useMemo(
    () => adjustmentItems.filter((item) => ["changed", "unchanged"].includes(getRowStatus(item, duplicatedIds))),
    [adjustmentItems, duplicatedIds]
  )

  const changedRows = useMemo(
    () => adjustmentItems.filter((item) => getRowStatus(item, duplicatedIds) === "changed"),
    [adjustmentItems, duplicatedIds]
  )

  const hasInvalidRows = useMemo(
    () => adjustmentItems.some((item) =>
      ["duplicate", "incomplete", "unloaded"].includes(getRowStatus(item, duplicatedIds))
    ),
    [adjustmentItems, duplicatedIds]
  )

  const canSubmitForm = useMemo(
    () => canSubmit && !!outletId && changedRows.length > 0 && !hasInvalidRows && !submitting && !loadingSystemStock,
    [canSubmit, outletId, changedRows.length, hasInvalidRows, submitting, loadingSystemStock]
  )
  const draftableRows = useMemo(
    () => adjustmentItems.filter((item) => ["changed", "unchanged"].includes(getRowStatus(item, duplicatedIds))),
    [adjustmentItems, duplicatedIds]
  )
  const canSaveDraft = useMemo(
    () => canSubmit && !!outletId && draftableRows.length > 0 && !duplicatedIds.length && !submitting && !loadingSystemStock,
    [canSubmit, outletId, draftableRows.length, duplicatedIds.length, submitting, loadingSystemStock]
  )

  const summary = useMemo(() => {
    const totalIncrease = changedRows.reduce((sum, item) => {
      const diff = Number(item.difference_qty)
      return diff > 0 ? sum + diff : sum
    }, 0)
    const totalDecrease = changedRows.reduce((sum, item) => {
      const diff = Number(item.difference_qty)
      return diff < 0 ? sum + Math.abs(diff) : sum
    }, 0)
    return { totalItems: validRows.length, changedItems: changedRows.length, totalIncrease, totalDecrease }
  }, [validRows, changedRows])

  // ── stock loading ────────────────────────────────────────────────────────

  const loadStockForRow = useCallback(async (productUnitId, currentOutletId) => {
    // console.log("Loading stock for product_unit_id", productUnitId, "and outlet_id", currentOutletId)
    if (!productUnitId || !currentOutletId) return null
    const response = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ product_unit_ids: [String(productUnitId)], outlet_id: Number(currentOutletId) }),
    })
    const json = await response.json()
    if (!response.ok || Number(json?.success) !== 1)
      throw new Error(json?.message || "Gagal mengambil system stock")
    const rows = Array.isArray(json?.data) ? json.data : []
    const found = rows[0]
    return Number(found?.current_stock ?? found?.stock ?? found?.balance ?? 0)
  }, [])

  const reloadStockForRow = async (key) => {
    const item = adjustmentItems.find((i) => i.key === key)
    if (!item || !item.product_unit_id || !outletId) return

    setAdjustmentItems((prev) =>
      prev.map((i) => i.key === key ? { ...i, is_loading_stock: true } : i)
    )
    try {
      const systemStock = await loadStockForRow(item.product_unit_id, outletId)
      setAdjustmentItems((prev) =>
        prev.map((i) =>
          i.key === key
            ? { ...recalcRow(i, i.physical_stock, systemStock), is_loading_stock: false, stock_loaded: true }
            : i
        )
      )
    } catch (error) {
      setAdjustmentItems((prev) =>
        prev.map((i) => i.key === key ? { ...i, is_loading_stock: false } : i)
      )
      toast.error(error?.message || "Gagal mengambil system stock")
    }
  }

  const loadAllStocks = async (targetOutletId = outletId) => {
    const productIds = adjustmentItems
      .map((item) => String(item.product_unit_id || "").trim())
      .filter(Boolean)

    if (!targetOutletId || !productIds.length) return

    try {
      setLoadingSystemStock(true)
      setAdjustmentItems((prev) =>
        prev.map((item) =>
          String(item.product_unit_id || "").trim()
            ? { ...item, is_loading_stock: true }
            : item
        )
      )
      const response = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          "X-Request-Id": `${Date.now()}-${Math.random()}`,
        },
        body: JSON.stringify({ product_unit_ids: productIds, outlet_id: Number(targetOutletId) }),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1)
        throw new Error(json?.message || "Gagal mengambil system stock")

      const rows = Array.isArray(json?.data) ? json.data : []
      const byId = rows.reduce((acc, row) => {
        const id = String(row?.product_unit_id ?? row?.product_unit?.id ?? "")
        if (!id) return acc
        acc[id] = row
        return acc
      }, {})

      setAdjustmentItems((prev) =>
        prev.map((item) => {
          const itemId = String(item.product_unit_id || "")
          const found = byId[itemId]
          const systemStock = Number(found?.current_stock ?? found?.stock ?? found?.balance ?? 0)
          return { ...recalcRow(item, item.physical_stock, systemStock), stock_loaded: !!itemId, is_loading_stock: false }
        })
      )
      return true
    } catch (error) {
      toast.error(error?.message || "Gagal memuat system stock")
      return false
    } finally {
      setLoadingSystemStock(false)
    }
  }

  // ── item mutation ────────────────────────────────────────────────────────

  const updateAdjustmentItem = (key, field, value) => {
    if (field === "physical_stock") {
      setAdjustmentItems((prev) =>
        prev.map((item) => item.key !== key ? item : recalcRow(item, value, item.system_stock))
      )
      return
    }
    setAdjustmentItems((prev) =>
      prev.map((item) => item.key !== key ? item : { ...item, [field]: value })
    )
  }

  const addAdjustmentItem = async () => {
    if (!selectedProductId) {
      toast.error("Pilih product unit terlebih dahulu")
      return
    }
    const alreadyAdded = adjustmentItems.some(
      (item) => String(item.product_unit_id) === String(selectedProductId)
    )
    if (alreadyAdded) {
      toast.error("Product ini sudah ada di daftar adjustment")
      return
    }
    const selected =
      selectedProductRow ||
      productUnitOptions.find((row) => String(row.id) === String(selectedProductId))
    const newItem = {
      ...createEmptyAdjustmentItem(),
      product_unit_id: selectedProductId,
      product_unit_name: selected?.long_name || selected?.name || "",
      product_unit_sku: [selected?.sku, selected?.product_model?.name].filter(Boolean).join(" · "),
      is_loading_stock: !!outletId,
    }
    setAdjustmentItems((prev) => [...prev, newItem])
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")
    if (outletId) {
      try {
        const systemStock = await loadStockForRow(selectedProductId, outletId)
        setAdjustmentItems((prev) =>
          prev.map((item) =>
            item.product_unit_id === selectedProductId && !item.stock_loaded
              ? { ...recalcRow(item, item.physical_stock, systemStock), is_loading_stock: false, stock_loaded: true }
              : item
          )
        )
      } catch (error) {
        setAdjustmentItems((prev) =>
          prev.map((item) =>
            item.product_unit_id === selectedProductId && item.is_loading_stock
              ? { ...item, is_loading_stock: false }
              : item
          )
        )
        toast.error(error?.message || "Gagal mengambil system stock")
      }
    }
  }

  const removeAdjustmentItem = (key) =>
    setAdjustmentItems((prev) => prev.filter((item) => item.key !== key))

  // ── outlet change (inline confirmation) ─────────────────────────────────

  const handleChangeOutlet = (value) => {
    if (hasFilledItems && value !== outletId) {
      setPendingOutletId(value)
      return
    }
    applyOutletChange(value)
  }

  const applyOutletChange = async (value) => {
    setOutletId(value)
    setPendingOutletId(null)
    setSelectedProductId("")
    setSelectedProductRow(null)
    setProductSearch("")
    setDebouncedProductSearch("")
    setAdjustmentItems((prev) =>
      prev.map((item) => ({
        ...item,
        system_stock: null,
        difference_qty: null,
        stock_loaded: false,
        is_loading_stock: false,
      }))
    )
    const validProductIds = adjustmentItems
      .map((item) => String(item.product_unit_id || "").trim())
      .filter(Boolean)
    if (value && validProductIds.length) {
      const ok = await loadAllStocks(value)
      if (ok) toast.success("System stock updated for new outlet")
    }
  }

  const cancelOutletChange = () => setPendingOutletId(null)

  // ── submit ───────────────────────────────────────────────────────────────

  const buildPayload = (items, status) => ({
    status,
    adjustment_date: moment(adjustmentDate).format("YYYY-MM-DD HH:mm:ss"),
    outlet_id: Number(outletId),
    outlet: selectedOutlet?.name || null,
    outlet_group_id: selectedOutlet?.outlet_group_id || selectedOutlet?.branch_group_id || null,
    outlet_group: selectedOutlet?.outlet_group?.name || selectedOutlet?.branch_group?.name || selectedOutlet?.outlet_group_name || null,
    notes: adjustmentNotes || null,
    items: items.map((item) => ({
      product_unit_id: String(item.product_unit_id || "").trim(),
      physical_stock: Number(item.physical_stock),
      notes: item.notes || "",
    })),
  })

  const handleSaveDraft = async () => {
    if (!outletId) { toast.error("Outlet wajib dipilih"); return }
    if (duplicatedIds.length) { toast.error("Product unit tidak boleh duplikat"); return }
    if (!draftableRows.length) { toast.error("Tambahkan minimal 1 item valid untuk draft"); return }

    const timestamp = new Date().toISOString()
    toast.loading("Saving draft stock adjustment...", { id: `toast-draft-${timestamp}` })

    try {
      setSubmitting(true)
      const payload = buildPayload(draftableRows, "DRAFT")
      const url = adjustmentId
        ? `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}`
        : `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments`
      const method = adjustmentId ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal menyimpan draft stock adjustment")
      }

      toast.success(json?.message || "Draft stock adjustment berhasil disimpan", { id: `toast-draft-${timestamp}` })
      onSuccess?.(json?.data)
    } catch (error) {
      toast.error(error?.message || "Gagal menyimpan draft stock adjustment", { id: `toast-draft-${timestamp}` })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!outletId) { toast.error("Outlet wajib dipilih"); return }
    if (duplicatedIds.length) { toast.error("Product unit tidak boleh duplikat"); return }
    if (!changedRows.length) { toast.error("Tidak ada item yang berubah"); return }
    if (hasInvalidRows) { toast.error("Masih ada item yang belum valid"); return }

    const timestamp = new Date().toISOString()
    toast.loading("Processing stock adjustment...", { id: `toast-${timestamp}` })

    try {
      setSubmitting(true)
      const payload = buildPayload(changedRows, "POSTED")
      const url = adjustmentId
        ? `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}`
        : `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments`
      const method = adjustmentId ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      })
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1)
        throw new Error(json?.message || "Gagal memproses stock adjustment")

      toast.success(json?.message || "Stock adjustment berhasil diproses", { id: `toast-${timestamp}` })
      onSuccess?.(json?.data)
      onClose?.()
    } catch (error) {
      toast.error(error?.message || "Gagal memproses stock adjustment", { id: `toast-${timestamp}` })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelDraft = async () => {
    if (!adjustmentId || currentAdjustmentStatus !== "DRAFT") return

    const confirmed = window.confirm("Batalkan draft stock adjustment ini?")
    if (!confirmed) return

    const toastId = `toast-cancel-draft-${Date.now()}`
    toast.loading("Cancelling stock adjustment draft...", { id: toastId })

    try {
      setSubmitting(true)
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${adjustmentId}/cancel`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )

      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal membatalkan draft stock adjustment")
      }

      toast.success(json?.message || "Draft stock adjustment berhasil dibatalkan", { id: toastId })
      onSuccess?.(json?.data)
      onClose?.()
    } catch (error) {
      toast.error(error?.message || "Gagal membatalkan draft stock adjustment", { id: toastId })
    } finally {
      setSubmitting(false)
    }
  }

  const handleManualRefreshAll = async () => {
    if (!outletId) { toast.error("Silakan pilih outlet terlebih dahulu"); return }
    const productIds = adjustmentItems.map((item) => String(item.product_unit_id || "").trim()).filter(Boolean)
    if (!productIds.length) { toast.error("Pilih minimal 1 product unit terlebih dahulu"); return }
    if (duplicatedIds.length) { toast.error("Terdapat product unit duplikat"); return }
    const ok = await loadAllStocks()
    if (ok) toast.success("System stock berhasil dimuat")
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit} className="flex flex-col w-full gap-5 mt-2">

      {/* ── Step 1: header info ── */}
      <div className="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-gray-800 rounded-full">1</span>
          <span className="text-sm font-semibold text-gray-700">Adjustment Details</span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <Label value="Outlet" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <SelectDescription
              selection={outletOptions}
              isLoading={isLoadingOutletSelection}
              value={outletId}
              onChange={handleChangeOutlet}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Select Outlet..."
            />
          </div>
          <div>
            <Label value="Adjustment Date" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <Input
              type="datetime-local"
              value={adjustmentDate}
              onChange={(e) => setAdjustmentDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="md:col-span-2">
            <Label value="Notes" className="mb-1.5 block text-sm font-medium text-gray-700" />
            <Input
              type="text"
              value={adjustmentNotes}
              onChange={(e) => setAdjustmentNotes(e.target.value)}
              placeholder="e.g. Stock opname gudang utama"
              className="w-full"
            />
          </div>
        </div>

        {/* Inline outlet change warning */}
        {pendingOutletId && (
          <div className="mt-4">
            <OutletChangeWarning
              pendingOutlet={pendingOutletId}
              pendingOutletName={pendingOutletName}
              onConfirm={() => applyOutletChange(pendingOutletId)}
              onCancel={cancelOutletChange}
            />
          </div>
        )}
      </div>

      {/* ── Step 2: items — gated on outlet ── */}
      {!outletId ? (
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div className="flex items-center gap-2 px-5 pt-5 mb-2">
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-gray-400 border-2 border-gray-200 rounded-full">2</span>
            <span className="text-sm font-semibold text-gray-400">Adjustment Items</span>
          </div>
          <OutletGate
            outletOptions={outletOptions}
            isLoadingOutletSelection={isLoadingOutletSelection}
            value={outletId}
            onChange={handleChangeOutlet}
          />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { label: "Valid Items",    value: summary.totalItems,    cls: "text-gray-900",  border: "border-gray-200",  bg: "bg-white" },
              { label: "Changed Items",  value: summary.changedItems,  cls: "text-blue-700",  border: "border-blue-200",  bg: "bg-white" },
              { label: "Total Increase", value: `+${summary.totalIncrease}`, cls: "text-green-700", border: "border-green-200", bg: "bg-green-50/40" },
              { label: "Total Decrease", value: `-${summary.totalDecrease}`, cls: "text-red-700",   border: "border-red-200",   bg: "bg-red-50/40"   },
            ].map(({ label, value, cls, border, bg }) => (
              <div key={label} className={`flex flex-col justify-center p-4 border ${border} ${bg} shadow-sm rounded-2xl`}>
                <div className={`text-xs font-semibold uppercase tracking-wide ${cls} opacity-70`}>{label}</div>
                <div className={`mt-1 text-3xl font-bold tracking-tight ${cls}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Items table */}
          <div className="relative z-20 flex flex-col bg-white border border-gray-200 shadow-sm rounded-2xl">
            {/* Table toolbar */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-gray-800 rounded-full">2</span>
                <span className="text-sm font-semibold text-gray-700">Adjustment Items</span>
                <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Only <strong>CHANGED</strong> rows are submitted
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:items-end">
                <div className="relative z-30 md:col-span-2">
                  <Label value="Search & Select Product" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
                  <SelectSearchable
                    selection={productUnitOptions}
                    isLoading={isLoadingProductUnitSelection}
                    value={selectedProductId}
                    onChange={(value, row) => {
                      const nextId = String(value || "")
                      setSelectedProductId(nextId)
                      setSelectedProductRow(row || null)
                    }}
                    keyValue={(v) => v.id}
                    title={(v) => v.long_name || v.name}
                    description={(v) => [v.sku, v.product_model?.name].filter(Boolean).join(" · ")}
                    placeholder="Search product unit / SKU..."
                    onSearchChange={(q) => setProductSearch(String(q || ""))}
                    serverSearch
                    clearOnClose={false}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleManualRefreshAll}
                  disabled={loadingSystemStock || submitting}
                  className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 transition-all bg-white border border-gray-300 shadow-sm rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 disabled:opacity-60 disabled:hover:bg-white"
                >
                  {loadingSystemStock ? "Loading..." : "Refresh All Stocks"}
                </button>
                <button
                  type="button"
                  onClick={addAdjustmentItem}
                  disabled={submitting || !selectedProductId}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white transition-all bg-gray-900 shadow-sm rounded-xl hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  + Add Item
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left whitespace-nowrap">
                <thead className="text-xs font-semibold tracking-wider text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">#</th>
                    <th className="px-4 py-3 min-w-[260px]">Product Unit</th>
                    <th className="px-4 py-3 min-w-[130px]">System Stock</th>
                    <th className="px-4 py-3 min-w-[150px]">Physical Stock</th>
                    <th className="px-4 py-3 min-w-[130px]">Difference</th>
                    <th className="px-4 py-3 min-w-[130px]">Status</th>
                    <th className="px-4 py-3 min-w-[240px]">Notes</th>
                    <th className="px-4 py-3 min-w-[80px]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {adjustmentItems.map((item, index) => {
                    const rowStatus = getRowStatus(item, duplicatedIds)
                    const isUnloaded = rowStatus === "unloaded"
                    const rowBg = rowStatus === "changed" ? "bg-blue-50/30" :
                                  rowStatus === "duplicate" ? "bg-red-50/30" :
                                  rowStatus === "incomplete" ? "bg-amber-50/20" :
                                  ""
                    return (
                      <tr key={item.key} className={`transition-colors hover:bg-gray-50/60 ${rowBg}`}>
                        <td className="px-4 py-3 text-gray-400 align-middle text-xs">{index + 1}</td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-800 leading-snug">
                              {item.product_unit_name || "-"}
                            </span>
                            {item.product_unit_sku && (
                              <span className="text-xs text-gray-400">{item.product_unit_sku}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center gap-1.5">
                            <div className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg min-w-[3.5rem]">
                              {item.is_loading_stock ? (
                                <span className="text-gray-400 animate-pulse">···</span>
                              ) : item.system_stock ?? "-"}
                            </div>
                            {/* Inline reload for UNLOADED rows */}
                            {isUnloaded && !item.is_loading_stock && (
                              <button
                                type="button"
                                onClick={() => reloadStockForRow(item.key)}
                                className="inline-flex items-center justify-center w-7 h-7 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Reload system stock"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <Input
                            type="number"
                            min="0"
                            value={item.physical_stock}
                            onChange={(e) => updateAdjustmentItem(item.key, "physical_stock", e.target.value)}
                            placeholder="0"
                            className="w-full text-center"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-bold rounded-lg border min-w-[4.5rem] ${
                            item.difference_qty === null
                              ? "bg-gray-50 border-gray-200 text-gray-400"
                              : Number(item.difference_qty) > 0
                              ? "bg-green-50 border-green-200 text-green-700"
                              : Number(item.difference_qty) < 0
                              ? "bg-red-50 border-red-200 text-red-700"
                              : "bg-gray-50 border-gray-200 text-gray-600"
                          }`}>
                            {item.difference_qty === null ? "-"
                              : Number(item.difference_qty) > 0 ? `+${item.difference_qty}`
                              : item.difference_qty}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <StatusBadge status={rowStatus} />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <Input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateAdjustmentItem(item.key, "notes", e.target.value)}
                            placeholder="Notes..."
                            className="w-full"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <button
                            type="button"
                            onClick={() => removeAdjustmentItem(item.key)}
                            disabled={adjustmentItems.length === 1 || submitting}
                            className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            title="Remove row"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                  {!adjustmentItems.length && (
                    <tr>
                      <td colSpan={8} className="px-5 py-12 text-center">
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <svg className="w-8 h-8 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
                            <path d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z"/>
                          </svg>
                          <p className="text-sm">Search a product above, then click <strong className="text-gray-600">+ Add Item</strong></p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Footer / submit bar ── */}
      <div className="flex flex-col items-center justify-between gap-3 pt-2 sm:flex-row">
        <SubmitBlocker
          outletId={outletId}
          changedRows={changedRows}
          hasInvalidRows={hasInvalidRows}
          duplicatedIds={duplicatedIds}
        />
        <div className="flex items-center w-full gap-3 sm:w-auto">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSaveDraft}
            className="w-full px-5 py-2.5 text-sm font-medium text-gray-800 transition-colors bg-amber-100 border border-amber-300 rounded-xl hover:bg-amber-200 focus:ring-2 focus:ring-amber-200 sm:w-auto disabled:opacity-60"
          >
            Save Draft
          </button>
          {adjustmentId && currentAdjustmentStatus === "DRAFT" && (
            <button
              type="button"
              onClick={handleCancelDraft}
              disabled={submitting || loadingSystemStock}
              className="w-full px-5 py-2.5 text-sm font-medium text-red-700 transition-colors bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 focus:ring-2 focus:ring-red-200 sm:w-auto disabled:opacity-60"
            >
              Cancel Draft
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || loadingSystemStock}
            className="w-full px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 sm:w-auto disabled:opacity-60"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!canSubmitForm}
            className="w-full px-6 py-2.5 text-sm font-medium text-white transition-all bg-gray-900 shadow-sm rounded-xl hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 sm:w-auto disabled:opacity-60"
          >
            {submitting ? "Processing..." : `Submit Adjustment${changedRows.length ? ` (${changedRows.length})` : ""}`}
          </button>
        </div>
      </div>
    </form>
  )
}

// ─── List page (unchanged except modal wiring) ────────────────────────────────

export default function List() {
  const { can } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef(null)
  const searchDebounceRef = useRef(null)

  const page          = searchParams.get("page")
  const search        = searchParams.get("search")
  const productIdsParam = searchParams.get("product_unit_ids")
  const outlet        = searchParams.get("outlet_id")
  const referenceType = searchParams.get("reference_type")
  const dateFrom      = searchParams.get("date_from")
  const dateTo        = searchParams.get("date_to")
  const orderBy       = searchParams.get("order_by")
  const direction     = searchParams.get("direction")
  const effectiveOrderBy = orderBy || "created_at"
  const effectiveDirection = direction || "desc"
  const shouldFetchList = Boolean(outlet)
  const selectedProductIds = useMemo(
    () => (productIdsParam ? productIdsParam.split(",").map((id) => id.trim()).filter(Boolean) : []),
    [productIdsParam]
  )
  const [inlineProductSearch, setInlineProductSearch] = useState("")
  const [inlineSelectedProductId, setInlineSelectedProductId] = useState("")
  const [selectedProductMetaById, setSelectedProductMetaById] = useState({})

  const [searchText, setSearchText] = useState(search ?? "")

  const branchParams = useMemo(() => ({ paginate: false }), [])
  const productParams = useMemo(
    () => ({
      paginate: true,
      search: inlineProductSearch || undefined,
    }),
    [inlineProductSearch]
  )
  const stockParams = useMemo(
    () => ({
      page,
      search,
      product_unit_ids: productIdsParam,
      outlet_id: outlet,
      reference_type: referenceType,
      date_from: dateFrom,
      date_to: dateTo,
      order_by: effectiveOrderBy,
      direction: effectiveDirection,
      enabled: shouldFetchList,
    }),
    [
      page,
      search,
      productIdsParam,
      outlet,
      referenceType,
      dateFrom,
      dateTo,
      effectiveOrderBy,
      effectiveDirection,
      shouldFetchList,
    ]
  )

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories(branchParams)

  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const {
    data: productUnitSelectionRaw,
    isLoading: isLoadingProductUnitSelection,
  } = useProductUnitRepositories(productParams)

  const productUnitOptions = useMemo(() => {
    if (Array.isArray(productUnitSelectionRaw)) return productUnitSelectionRaw
    if (Array.isArray(productUnitSelectionRaw?.data)) return productUnitSelectionRaw.data
    return []
  }, [productUnitSelectionRaw])

  const { data: stockRaw, isLoading } = useStockTransactionRepositories(stockParams)

  const stockData = useMemo(() => stockRaw ?? {}, [stockRaw])
  const stockRows = useMemo(() => stockData?.data ?? [], [stockData])

  useEffect(() => {
    if (!productUnitOptions.length && !stockRows.length) {
      return
    }

    setSelectedProductMetaById((prev) => {
      const next = { ...prev }

      productUnitOptions.forEach((row) => {
        const id = String(row?.id ?? "")
        if (!id) return
        next[id] = row
      })

      stockRows.forEach((row) => {
        const id = String(row?.product_unit_id ?? "")
        if (!id || next[id]) return

        next[id] = {
          id,
          long_name:
            row?.product_unit?.long_name ||
            row?.product_unit_name ||
            row?.product_unit?.name ||
            row?.product_unit ||
            id,
          name:
            row?.product_unit?.name ||
            row?.product_unit_name ||
            row?.product_unit ||
            id,
          sku: row?.product_unit?.sku || row?.sku || "",
        }
      })

      return next
    })
  }, [productUnitOptions, stockRows])

  const selectedInlineProducts = useMemo(
    () =>
      selectedProductIds.map((id) => {
        const resolved = selectedProductMetaById[String(id)]
        return resolved || { id, long_name: id, name: id, sku: id }
      }),
    [selectedProductIds, selectedProductMetaById]
  )

  useEffect(() => { isLoading ? nProgress.start() : nProgress.done() }, [isLoading])
  useEffect(() => { searchInput.current?.focus() }, [])
  useEffect(() => { setSearchText(search ?? "") }, [search])
  useEffect(() => () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) }, [])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") delete merged[key]
    })

    const currentSerialized = new URLSearchParams(current).toString()
    const nextSerialized = new URLSearchParams(merged).toString()

    if (currentSerialized === nextSerialized) {
      return
    }

    setSearchParams(merged)
  }

  const commitSearch = (value) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    mergeParams({ search: value || null, page: 1 })
  }

  const updateSearch = (value) => {
    setSearchText(value)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => mergeParams({ search: value || null, page: 1 }), 400)
  }

  const clearSearch = () => { setSearchText(""); commitSearch(""); searchInput.current?.focus() }

  const toggleSort = (value) => {
    if ((effectiveDirection === "desc") || effectiveOrderBy !== value) {
      mergeParams({ order_by: value, direction: "asc", page: 1 })
    } else {
      mergeParams({ order_by: value, direction: "desc", page: 1 })
    }
  }

  const dataListHeaders = [
    { isAction: false, content: "Reference",        sortKey: "reference_id"    },
    { isAction: false, content: "Product",          sortKey: "product_unit_id" },
    { isAction: false, content: "Outlet",           sortKey: "outlet_id"       },
    { isAction: false, content: "Type",             sortKey: "reference_type"  },
    { isAction: false, content: "Qty",              sortKey: "quantity"        },
    { isAction: false, content: "Balance",          sortKey: "current_stock"   },
    { isAction: false, content: "Transaction Date", sortKey: "transaction_date"},
    { isAction: false, content: "Created By",       sortKey: "created_by"      },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="flex flex-col gap-1">
          <span>{v.reference_id || "-"}</span>
          {Number(v._grouped_count ?? 1) > 1 && (
            <span className="text-[11px] font-medium text-blue-600">
              FIFO split grouped: {v._grouped_count} rows
            </span>
          )}
        </div>
      ),
    },
    {
      type: "default",
      content: (v) =>
        v.product_unit?.long_name ||
        v.product_unit_name ||
        v.product_unit?.name ||
        v.product_unit ||
        v.product_unit_id ||
        "-",
    },
    { type: "default", content: (v) => v.outlet?.name || "-" },
    { type: "default", content: (v) => (
        <span className="px-2 py-1 text-xs rounded bg-neutral-100">{v.reference_type}</span>
      ),
    },
    { type: "default", content: (v) => {
        const qty = Number(v.quantity ?? 0)
        return <span className={qty >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{qty >= 0 ? `+${qty}` : qty}</span>
      },
    },
    { type: "default", content: (v) => <span className="font-semibold text-neutral-700">{v.balance ?? v.current_stock ?? "-"}</span> },
    { type: "default", content: (v) => v.transaction_date ? moment(v.transaction_date).format("YYYY-MM-DD HH:mm") : "-" },
    { type: "default", content: (v) => v.created_by || "-" },
  ]

  const updateFilter = (value) => mergeParams({ ...value, page: 1 })
  const removeFilter = () => mergeParams({
    reference_type: null,
    date_from: null,
    date_to: null,
    page: 1,
  })
  const addInlineProductFilter = () => {
    if (!inlineSelectedProductId) {
      toast.error("Pilih product terlebih dahulu")
      return
    }

    const nextIds = Array.from(new Set([...selectedProductIds, String(inlineSelectedProductId)]))
    mergeParams({
      product_unit_ids: nextIds.join(","),
      product_unit_id: null,
      page: 1,
    })
    setInlineSelectedProductId("")
    setInlineProductSearch("")
  }

  const removeInlineProductFilter = (productId) => {
    const nextIds = selectedProductIds.filter((id) => String(id) !== String(productId))
    mergeParams({
      product_unit_ids: nextIds.length ? nextIds.join(",") : null,
      page: 1,
    })
  }

  const handleOutletInlineChange = (value) => {
    if (String(value || "") === String(outlet || "")) {
      return
    }

    mergeParams({
      outlet_id: value || null,
      page: 1,
    })
  }

  const handleExport = () => {
    if (!outlet) {
      toast.error("Pilih outlet terlebih dahulu")
      return
    }
    const params = new URLSearchParams(searchParams)
    params.set("report_type", "stock_movement")
    window.open(`${process.env.REACT_APP_INVENTORY_API_URL}/stock/transfer/export?${params.toString()}`, "_blank")
  }

  return (
    <>
      <div className="space-y-6">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl space-y-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[240px_minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-end">
            <div>
              <Label value="Outlet" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <SelectDescription
                selection={outletOptions}
                isLoading={isLoadingOutletSelection}
                value={outlet}
                onChange={handleOutletInlineChange}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={(v) => v.code || v.id}
                placeholder="Select Outlet"
              />
            </div>

            <div>
              <Label value="Product Filter" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <SelectSearchable
                selection={productUnitOptions}
                isLoading={isLoadingProductUnitSelection}
                value={inlineSelectedProductId}
                onChange={(value) => setInlineSelectedProductId(String(value || ""))}
                keyValue={(v) => v.id}
                title={(v) => v.long_name || v.name}
                description={(v) => [v.sku, v.product_model?.name].filter(Boolean).join(" · ")}
                placeholder="Search product unit / SKU..."
                onSearchChange={(value) => setInlineProductSearch(value)}
                serverSearch
                clearOnClose={false}
                disabled={!outlet}
              />
            </div>

            <button
              type="button"
              onClick={addInlineProductFilter}
              disabled={!outlet || !inlineSelectedProductId}
              className="px-4 py-3 text-sm font-medium text-white transition bg-neutral-800 rounded-xl disabled:opacity-60"
            >
              Add Product
            </button>

            <div className="relative">
              <Label value="Search Reference" className="mb-1.5 block text-xs font-medium text-gray-500 uppercase tracking-wide" />
              <input
                ref={searchInput}
                onChange={(e) => updateSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitSearch(searchText) }}
                value={searchText}
                type="text"
                placeholder="Search reference..."
                disabled={!outlet}
                className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              />
              {!!searchText && (
                <button type="button" onClick={clearSearch}
                  className="absolute inset-y-0 right-3 top-6 flex items-center text-neutral-400 hover:text-neutral-700">✕</button>
              )}
            </div>
          </div>

          {!!selectedInlineProducts.length && (
            <div className="flex flex-wrap items-center gap-2">
              {selectedInlineProducts.map((productItem) => (
                <button
                  key={productItem.id}
                  type="button"
                  onClick={() => removeInlineProductFilter(productItem.id)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full"
                >
                  <span>{productItem.long_name || productItem.name || productItem.id}</span>
                  <span>✕</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Filter
              onSubmit={updateFilter}
              onRemove={removeFilter}
              data={Object.fromEntries(
                Object.entries({
                  reference_type: referenceType,
                  date_from: dateFrom,
                  date_to: dateTo,
                }).filter(([, v]) => v != null)
              )}
            />
            {/* {can("stock_transaction.export") && ( */}
              <button onClick={handleExport}
                className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-green-600 rounded-xl active:hover:scale-95">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none">
                  <path d="M12 3v12"/><path d="M7 10l5 5l5 -5"/><path d="M5 21h14"/>
                </svg>
                <span>Export</span>
              </button>
            {/* )} */}
          </div>
        </div>
        {!outlet ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white border border-neutral-200 rounded-2xl text-center">
            <p className="text-base font-semibold text-neutral-800">Pilih outlet terlebih dahulu</p>
            <p className="mt-1 text-sm text-neutral-500">
              Setelah outlet dipilih, Anda bisa filter produk yang ingin ditampilkan.
            </p>
          </div>
        ) : (
          <>
            <Table
              isLoading={isLoading}
              data={stockRows}
              headers={dataListHeaders}
              columns={dataListColumns}
              key={(v) => `${v.reference_id}-${v.product_unit_id}-${v.transaction_date}-${v._grouped_count ?? 1}`}
              isEmpty={!stockRows.length && !search}
              isEmptyOnSearching={!stockRows.length && !!search}
              toggleSort={toggleSort}
              orderBy={effectiveOrderBy}
              direction={effectiveDirection}
            />
            <Pagination links={stockData?.links} from={stockData?.from} to={stockData?.to} total={stockData?.total} />
          </>
        )}
      </div>

    </>
  )
}
